import { Button, Modal } from "@/components/ui";
import { useFormik } from "formik";
import { RouteConstant } from "@/util/constant/RouteConstant";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { auth, AuthState } from "@/store/modules/auth";
import { Textfield } from "@/components/ui/input/textfield";
import { useEffect, useState } from "react";
import { BaseResponse } from "@/model/response/BaseResponse.ts";
import { EyeClosed, EyeIcon } from "lucide-react";
import { resetPasswordSchema } from "@/utilities/validation/authSchema";
import { CompletePasswordReset } from "@/model/request/auth/CompletePasswordReset";

export const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const authState = useSelector<any>((state) => state.auth) as AuthState;
  const location = useLocation();
  const userEmailFromLocation = location.state?.userEmail;
  const [seePassword, setSeePassword] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [response, setResponse] = useState("");
  const [successResponse, setSuccessResponse] = useState("");
  const [successModal, setSuccessModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const secondsFormatted = seconds > 9 ? seconds : `0${seconds}`;

  const showSuccessMessage = (res: any) => {
    setSuccessResponse(res.responseMessage);
    setSuccessModal(true);
  };

  const showMessageError = (res: any) => {
    setResponse(res.responseMessage);
    setModalVisible(true);
  };

  const handleSubmit = async (value: CompletePasswordReset) => {
    console.log(value)
    const request = { ...value, userEmail: userEmailFromLocation };
    await dispatch(
      auth.action.resetPassword({
        data: request,
        onSuccess: (response: BaseResponse) => showSuccessMessage(response),
        onError: (response: BaseResponse) => showMessageError(response),
      })
    );
  };

  const resendOtp = () => {
    dispatch(
      auth.action.resendOtp({
        data: { userEmail: authState.userEmail },
        onSuccess: showSuccessMessage,
        onError: showMessageError,
      })
    );
    setTimeLeft(60); // Reset timer after resend
  };

  const formik = useFormik({
    initialValues: {
      otp: "",
      userPassword: "",
      userEmail: userEmailFromLocation||"",
    },
    onSubmit: handleSubmit,
    validationSchema: resetPasswordSchema,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="p-5 space-y-5">
      <Textfield
        label="OTP"
        placeholder="Enter OTP sent to Email"
        name="otp"
        type="text"
        inputMode="numeric"
        error={formik.touched.otp && formik.errors.otp}
        formik={formik}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/\D/g, "");
        }}
      />

      <Textfield
        label="Password"
        name="userPassword"
        placeholder="Create new password"
        type={seePassword ?  "password":"text" }
        error={formik.touched.userPassword && formik.errors.userPassword}
        formik={formik}
        suffixIcon={
          <button
            type="button"
            onClick={() => setSeePassword((prev) => !prev)}
          >
            {seePassword ? (
              <EyeClosed color="black" />
            ) : (
              <EyeIcon color="black" />
            )}
          </button>
        }
      />

      <Button
        label="Complete Password Reset"
        className="w-full mt-5"
        state="primary"
        type="submit"
        isLoading={authState.loading}
        disabled={!formik.isValid || authState.loading}
      />

      {timeLeft <= 30 && (
        <div className="text-center mt-3">
          <p>
            Didn't get a code?
            <button
              onClick={resendOtp}
              type="button"
              className="text-secondary ml-2"
            >
              Resend OTP ({minutes}:{secondsFormatted})
            </button>
          </p>
        </div>
      )}

      <Modal
        text={successResponse}
        type="success"
        modal={successModal}
        buttonLabel="Proceed"
        onClose={() => navigate(RouteConstant.auth.login.path)}
        onClick={() => navigate(RouteConstant.auth.login.path)}
      />

      <Modal
        text={response}
        type="error"
        modal={modalVisible}
        onClose={() => setModalVisible(false)}
        buttonLabel="Close"
        onClick={() => setModalVisible(false)}
      />
    </form>
  );
};
